import {
  Root as PopRoot,
  Trigger as PopTrigger,
  Content as PopContent,
  Portal as PopPortal,
} from '@radix-ui/react-popover';
import { mdiAccountCircle, mdiCheck, mdiLogout } from '@mdi/js';
import { Icon } from '@mdi/react';
import React, { forwardRef, startTransition, useMemo, useState } from 'react';
import { styled, keyframes } from 'styled-components';
import {
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxItem,
  ComboboxProvider,
} from '@ariakit/react';
import { matchSorter } from 'match-sorter';
import { Link } from 'react-router-dom';
import { trackEvent } from 'lib/amplitude';
import { ComboboxSearch, Truncate } from 'components/ui';

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px) scale(0);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const MenuTrigger = styled(PopTrigger)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: #92a6be;

  &:hover {
    color: #fff;
    background: #8f99a826;
    cursor: pointer;
  }
`;

const IconAvatar = styled(Icon).attrs({ path: mdiAccountCircle })`
  width: 18px;
  height: 18px;
  color: inherit;
`;

const IconChecked = styled(Icon).attrs({
  path: mdiCheck,
})`
  width: 16px;
  height: 16px;
  color: #215db0;
  margin-right: 7px;
  visibility: hidden;

  &[data-state='checked'] {
    visibility: visible;
  }
`;

const IconLogout = styled(Icon).attrs({
  path: mdiLogout,
})`
  width: 16px;
  height: 16px;
  margin-right: 7px;
  color: inherit;
`;

const MenuContent = styled(PopContent)`
  background: #fff;
  animation: 400ms ${slideDownAndFade} cubic-bezier(0.16, 1, 0.3, 1);
  will-change: opacity;
  position: relative;
  top: 2px;
  z-index: 20;
  min-width: 180px;
  max-height: calc(100vh - 64px);
  overflow-y: scroll;
  box-shadow:
    0 0 0 1px rgb(17 20 24 / 10%),
    0 2px 4px rgb(17 20 24 / 20%),
    0 8px 24px rgb(17 20 24 / 20%);
`;

const Section = styled.section`
  border-bottom: 1px solid #e0e0e0;
  padding: 5px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const GroupLabel = styled(ComboboxGroupLabel)`
  color: #1c2127;
  font-size: 14px;
  font-weight: 600;
  margin: 5px;
  padding: 0 5px;
`;

const MenuItem = styled(ComboboxItem)`
  display: flex;
  align-items: center;
  line-height: 20px;
  color: #1c2127;
  font-size: 12px;
  padding: 7px 5px;
  margin: 0 5px;
  text-decoration: none;
  cursor: pointer;

  &:hover:not([data-state='checked']) {
    background-color: #8f99a826;
  }

  &[data-state='checked'] {
    background-color: #2d72d21a;
    color: #215db0;
    cursor: default;
  }
`;

/**
 * Handles menu selection
 *
 * @param {string} value - value of the selected menu item
 */
const handleSelect = (value) => {
  if (value === 'logout') {
    trackEvent('Logged Out');
    return;
  }
  trackEvent('Organization Selected', { orgKey: value });
};

/**
 * Organization menu component
 *
 * @param {object} props -
 * @param {string} props.orgKey - organization key
 * @param {Array} props.portfolios - array of portfolios with organizations
 * @returns {JSX.Element} - Organization menu component
 */
const OrgMenu = forwardRef(function OrgMenu(
  { orgKey, portfolios, ...props },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const matches = useMemo(() => {
    if (!searchValue) {
      return portfolios;
    }
    return portfolios
      .map(({ organizations, ...portfolio }) => ({
        ...portfolio,
        organizations: matchSorter(organizations, searchValue, {
          keys: ['name'],
        }),
      }))
      .filter(({ organizations }) => organizations.length > 0);
  }, [portfolios, searchValue]);
  return (
    <PopRoot open={open} onOpenChange={setOpen}>
      <MenuTrigger {...props} ref={ref} aria-label="Organization Menu">
        <IconAvatar />
      </MenuTrigger>
      <PopPortal>
        <MenuContent>
          <ComboboxProvider
            open={open}
            setOpen={setOpen}
            resetValueOnHide
            selectedValue={orgKey}
            setSelectedValue={handleSelect}
            setValue={(value) =>
              startTransition(() => {
                setSearchValue(value);
              })
            }
          >
            <ComboboxSearch />
            <Section>
              {matches.map((portfolio) => (
                <ComboboxGroup key={portfolio.key}>
                  <GroupLabel>
                    <Truncate>{portfolio.name}</Truncate>
                  </GroupLabel>
                  <>
                    {portfolio.organizations.map(({ key, name }) => {
                      const dataState = orgKey === key ? 'checked' : '';
                      return (
                        <MenuItem
                          key={key}
                          value={key}
                          hideOnClick
                          data-state={dataState}
                          disabled={orgKey === key}
                          render={<Link to={`/organization/${key}`} />}
                        >
                          <IconChecked data-state={dataState} />
                          <Truncate>{name}</Truncate>
                        </MenuItem>
                      );
                    })}
                  </>
                </ComboboxGroup>
              ))}
            </Section>
            <Section>
              <MenuItem
                value="logout"
                hideOnClick
                render={
                  <Link
                    to={`${import.meta.env.UF_API_SERVER_ADDR}/api/signout`}
                  />
                }
              >
                <IconLogout />
                Log out
              </MenuItem>
            </Section>
          </ComboboxProvider>
        </MenuContent>
      </PopPortal>
    </PopRoot>
  );
});

export default OrgMenu;
